import React, { useState, useEffect, useRef } from 'react';
import { Search, X, UserPlus, Send, Clock, Check, CheckCheck, User, AlertTriangle, UserX, Smile, Paperclip, Mic, CornerUpLeft } from 'lucide-react';
import './Match.css';
import ReportModal from './ReportModal';
import UserProfile from './UserProfile';
import { triggerHaptic } from '../utils/haptics';
import { useChat } from '../context/ChatContext';
import { VipName } from './VipName';


const Match = ({ user, isSubscribed, channelUrl, onRequestSubscription }) => {
  const {
      socket,
      matchStatus,
      onlineCount,
      partner,
      partnerId,
      partnerIsVip,
      partnerCustomNameColor,
      partnerAvatarUrl,
      messages,
      typing,
      subscriptionRequiredData,
      areFriends,
      findMatch,
      skipMatch,
      skipAndBlockMatch,
      stopSearch,
      sendMessage,
      sendMediaMessage,
      sendTyping,
      clearSubscriptionError
  } = useChat();

  const [inputText, setInputText] = useState('');
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [viewProfileUserId, setViewProfileUserId] = useState(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(null); // { type, title, desc, onConfirm }
  const emojis = ['😊', '😂', '❤️', '👍', '😍', '🔥', '🎉', '😭', '🤔', '🙌', '👏', '✨', '🚀', '🌟', '💯', '👀', '🌹', '💔', '🎈', '✌️'];
  const messagesEndRef = useRef(null);
  const messagesListRef = useRef(null);

  // Redesign state additions
  const [showAttachmentMenu, setShowAttachmentMenu] = useState(false);
  const [recordedAudioUrl, setRecordedAudioUrl] = useState(null);
  const [recordedAudioBlob, setRecordedAudioBlob] = useState(null);
  const [isPlayingPreview, setIsPlayingPreview] = useState(false);
  const [replyingToMessage, setReplyingToMessage] = useState(null);
  const [activeOptionMenuMessageId, setActiveOptionMenuMessageId] = useState(null);

  const previewAudioRef = useRef(null);
  const touchStartX = useRef(0);
  const swipedMessageRef = useRef(null);
  const [swipeOffset, setSwipeOffset] = useState(0);
  const [swipedMessageId, setSwipedMessageId] = useState(null);

  // Media Recording & File Upload States
  const [isRecording, setIsRecording] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [uploadingMedia, setUploadingMedia] = useState(false);

  const photoInputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingTimerRef = useRef(null);

  // التمرير التلقائي لأسفل المحادثة
  const scrollToBottom = () => {
    if (messagesListRef.current) {
      messagesListRef.current.scrollTo({
        top: messagesListRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
  };

  useEffect(() => {
    if (matchStatus === 'matched') {
      scrollToBottom();
    }
  }, [messages, typing, matchStatus]);

  const handlePhotoClick = () => {
    if (!user?.isVip) {
      alert("هذه الميزة مخصصة لمشتركي VIP فقط! 🌟");
      return;
    }
    photoInputRef.current.click();
  };

  const handlePhotoSelected = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      alert("الحد الأقصى لحجم الصورة هو 10 ميجابايت.");
      return;
    }

    setUploadingMedia(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await fetch('/api/chat/upload', {
        method: 'POST',
        body: formData
      });
      if (res.ok) {
        const data = await res.json();
        sendMediaMessage(data.url, 'image', replyingToMessage);
        setReplyingToMessage(null);
      } else {
        alert("فشل رفع الصورة");
      }
    } catch (err) {
      console.error(err);
      alert("حدث خطأ أثناء رفع الصورة");
    } finally {
      setUploadingMedia(false);
      e.target.value = ''; // Reset input
    }
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const startVoiceRecording = async () => {
    if (!user?.isVip) {
      alert("هذه الميزة مخصصة لمشتركي VIP فقط! 🌟");
      return;
    }

    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      alert("متصفحك لا يدعم تسجيل الصوت.");
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioChunksRef.current = [];
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach(track => track.stop());

        if (audioChunksRef.current.length === 0) return;

        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        setRecordedAudioBlob(audioBlob);
        const audioUrl = URL.createObjectURL(audioBlob);
        setRecordedAudioUrl(audioUrl);
      };

      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start();
      setIsRecording(true);
      setRecordingDuration(0);

      recordingTimerRef.current = setInterval(() => {
        setRecordingDuration(prev => prev + 1);
      }, 1000);
    } catch (err) {
      console.error('Mic access error:', err);
      alert("لا يمكن الوصول للميكروفون. يرجى تفعيل الصلاحية.");
    }
  };

  const stopVoiceRecording = (shouldSend = true) => {
    if (!mediaRecorderRef.current || mediaRecorderRef.current.state === 'inactive') return;

    clearInterval(recordingTimerRef.current);
    setIsRecording(false);

    if (shouldSend) {
      mediaRecorderRef.current.stop();
    } else {
      const stream = mediaRecorderRef.current.stream;
      stream.getTracks().forEach(track => track.stop());
      mediaRecorderRef.current = null;
      audioChunksRef.current = [];
    }
  };

  const discardVoicePreview = () => {
    if (recordedAudioUrl) {
      URL.revokeObjectURL(recordedAudioUrl);
    }
    setRecordedAudioUrl(null);
    setRecordedAudioBlob(null);
    setIsPlayingPreview(false);
  };

  const sendVoicePreview = async () => {
    if (!recordedAudioBlob) return;

    setUploadingMedia(true);
    const audioFile = new File([recordedAudioBlob], 'voice-recording.webm', { type: 'audio/webm' });
    const formData = new FormData();
    formData.append('file', audioFile);

    try {
      const res = await fetch('/api/chat/upload', {
        method: 'POST',
        body: formData
      });
      if (res.ok) {
        const data = await res.json();
        sendMediaMessage(data.url, 'audio', replyingToMessage);
        setReplyingToMessage(null);
      } else {
        alert("فشل رفع التسجيل الصوتي");
      }
    } catch (err) {
      console.error(err);
      alert("حدث خطأ أثناء رفع التسجيل الصوتي");
    } finally {
      setUploadingMedia(false);
      discardVoicePreview();
    }
  };

  const togglePlayPreview = () => {
    if (!previewAudioRef.current) return;
    if (isPlayingPreview) {
      previewAudioRef.current.pause();
      setIsPlayingPreview(false);
    } else {
      previewAudioRef.current.play();
      setIsPlayingPreview(true);
    }
  };

  // Swipe-to-reply touch events
  const handleTouchStart = (e, msg) => {
    touchStartX.current = e.touches[0].clientX;
    swipedMessageRef.current = msg;
    setSwipedMessageId(msg.id || msg.dbId);
  };

  const handleTouchMove = (e) => {
    if (!swipedMessageRef.current) return;
    const currentX = e.touches[0].clientX;
    const diffX = touchStartX.current - currentX;
    if (diffX > 0) {
      const offset = Math.min(diffX, 80);
      setSwipeOffset(offset);
    }
  };

  const handleTouchEnd = () => {
    if (swipedMessageRef.current && swipeOffset >= 50) {
      const msg = swipedMessageRef.current;
      setReplyingToMessage({
        id: msg.id || msg.dbId,
        text: msg.mediaType === 'image' ? '📷 صورة' : (msg.mediaType === 'audio' ? '🎙️ رسالة صوتية' : msg.text),
        senderName: msg.sender === 'me' ? 'أنا' : (partner || 'الشريك')
      });
      triggerHaptic('light');
    }
    setSwipeOffset(0);
    swipedMessageRef.current = null;
    setSwipedMessageId(null);
  };

  const handleReplySnippetClick = (replyToId) => {
    const element = document.getElementById(`msg-${replyToId}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      element.classList.add('message-highlight');
      setTimeout(() => {
        element.classList.remove('message-highlight');
      }, 1500);
    }
  };

  const handleSendMessage = (e) => {
    e.preventDefault();
    if (inputText.trim()) {
      triggerHaptic('light');
      sendMessage(inputText, replyingToMessage);
      setInputText('');
      setShowEmojiPicker(false);
      setReplyingToMessage(null);
    }
  };

  const handleInputChange = (e) => {
    setInputText(e.target.value);
    sendTyping();
  };

  const handleAddFriend = () => {
    setConfirmDialog({
      type: 'friend',
      title: '➕ إضافة صديق',
      desc: 'هل تريد إرسال طلب صداقة لهذا الشخص؟ ستتمكن من التواصل معه في أي وقت عبر قسم الأصدقاء.',
      onConfirm: () => {
        if (socket) socket.emit('send_friend_request');
        alert('تم إرسال طلب الصداقة.');
      }
    });
  };

  const handleSkipMatch = () => {
    setConfirmDialog({
      type: 'skip',
      title: '⏭️ تخطي المحادثة',
      desc: 'هل تريد تخطي هذه المحادثة والبحث عن شخص جديد؟ ستنتهي المحادثة الحالية.',
      onConfirm: skipMatch
    });
  };

  const handleBlockMatch = () => {
    setConfirmDialog({
      type: 'block',
      title: '⛔ حظر المطابقة مجدداً',
      desc: 'هل تريد حظر المطابقة مع هذا الشخص لمدة 24 ساعة؟ لن تتقابلا مجدداً خلال هذه الفترة.',
      onConfirm: skipAndBlockMatch
    });
  };

  const handleReport = async (reason) => {
      try {
          const reportData = {
              reporterId: user?.id || user?.telegramId,
              reportedEntityId: partnerId || 'unknown_partner',
              entityType: 'user',
              reason: reason,
              messages: messages.slice(-10)
          };

          const res = await fetch('/api/reports', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(reportData)
          });

          if (res.ok) alert('تم إرسال البلاغ بنجاح. شكراً لك.');
          else alert('حدث خطأ أثناء إرسال البلاغ.');
      } catch (e) {
          console.error("Error submitting report", e);
      }
  };

  // دالة مساعدة لرسم أيقونة حالة الرسالة
  const renderMessageStatus = (status) => {
      switch (status) {
          case 'sending': return <Clock size={12} className="status-icon text-gray-400" />;
          case 'sent': return <Check size={14} className="status-icon text-gray-300" />;
          case 'delivered': return <CheckCheck size={14} className="status-icon text-gray-300" />;
          case 'read': return <CheckCheck size={14} className="status-icon text-blue-400" />;
          default: return null;
      }
  };

  return (
    <div className="match-main-container">
      {/* العداد متواجد دائماً في الأعلى - يختفي عند بدء المحادثة */}
      {matchStatus !== 'matched' && (
        <div className="global-online-counter">
          <span className="pulse-dot"></span>
          <span className="counter-text">{onlineCount} متصل الآن</span>
        </div>
      )}

      {/* الحالة 1: الخمول (جاهز للبحث) */}
      {matchStatus === 'idle' && (
        <div className="state-wrapper idle-state">
          <div className="radar-icon-container">
              <Search size={48} className="text-blue-400" />
          </div>
          <h2 className="state-title">البحث والمطابقة</h2>
          <p className="state-desc">ابحث عن شخص مجهول للتحدث معه بسرية تامة</p>
          <button className="btn-primary-large" onClick={findMatch}>
            بدء البحث
          </button>
        </div>
      )}

      {/* الحالة 2: جاري البحث */}
      {matchStatus === 'searching' && (
        <div className="state-wrapper searching-state">
          <div className="radar-animation">
              <div className="circle circle-1"></div>
              <div className="circle circle-2"></div>
              <div className="circle circle-3"></div>
              <User size={32} className="radar-center-icon" />
          </div>
          <h2 className="state-title mt-4">جاري البحث...</h2>
          <p className="state-desc">يرجى الانتظار، نبحث عن شريك متاح</p>
          <button className="btn-danger-large" onClick={stopSearch}>
            إيقاف البحث
          </button>
        </div>
      )}

      {/* الحالة 3: تم العثور على شريك (غرفة الدردشة) */}
      {matchStatus === 'matched' && (
        <div className="chat-interface">
          {/* هيدر الدردشة الثنائي */}
          <div className="chat-header">
            <div className="chat-partner-info" onClick={() => areFriends && setViewProfileUserId(partnerId)} style={{ cursor: areFriends ? 'pointer' : 'default' }}>
              <div className="partner-avatar">
                  {partnerAvatarUrl ? (
                      <img src={partnerAvatarUrl} alt="avatar" className="partner-avatar-img" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                  ) : (
                      <User size={20} />
                  )}
              </div>
              <div className="partner-details">
                  <h3>
                      <VipName user={{ nickname: partner, isVip: partnerIsVip, customNameColor: partnerCustomNameColor }} />
                  </h3>
                  <span className="typing-text">{typing ? 'يكتب الآن...' : 'متصل'}</span>
              </div>
            </div>
            <div className="chat-actions">
              <button className="action-icon-btn add-friend" onClick={handleAddFriend} title="إضافة صديق">
                  <UserPlus size={18} />
              </button>
              <button className="action-icon-btn block-24h" onClick={handleBlockMatch} title="تخطي وعدم المطابقة مجدداً">
                  <UserX size={18} />
              </button>
              <button className="action-icon-btn skip" onClick={handleSkipMatch} title="تخطي">
                  <X size={18} />
              </button>
              <button className="action-icon-btn report" onClick={() => setIsReportModalOpen(true)} title="إبلاغ">
                  <AlertTriangle size={18} />
              </button>
            </div>
          </div>

          {/* منطقة الرسائل */}
          <div className="messages-list" ref={messagesListRef}>
             <div className="system-message">
                 تمت المطابقة بنجاح! قل مرحباً 👋
             </div>
             
             {messages.map(msg => (
                 <div 
                   key={msg.id} 
                   id={`msg-${msg.id || msg.dbId}`}
                   className={`message-wrapper ${msg.sender === 'me' ? 'sent' : 'received'}`}
                   onTouchStart={(e) => handleTouchStart(e, msg)}
                   onTouchMove={(e) => handleTouchMove(e)}
                   onTouchEnd={handleTouchEnd}
                   style={{
                     transform: swipedMessageId === (msg.id || msg.dbId) ? `translateX(-${swipeOffset}px)` : 'none',
                     transition: swipedMessageId === (msg.id || msg.dbId) && swipeOffset !== 0 ? 'none' : 'transform 0.2s cubic-bezier(0.175, 0.885, 0.32, 1.275)'
                   }}
                 >
                    <div 
                      className="message-bubble"
                      onClick={() => setActiveOptionMenuMessageId(prev => prev === (msg.id || msg.dbId) ? null : (msg.id || msg.dbId))}
                    >
                        {/* Reply Indicator Snippet */}
                        {msg.replyTo && (
                          <div 
                            className="message-reply-snippet" 
                            onClick={(e) => {
                              e.stopPropagation();
                              handleReplySnippetClick(msg.replyTo.id);
                            }}
                          >
                            <span className="reply-sender">{msg.replyTo.senderName}</span>
                            <span className="reply-text">{msg.replyTo.text}</span>
                          </div>
                        )}

                        {msg.mediaType === 'image' ? (
                            <img 
                                src={msg.text} 
                                className="chat-media-image" 
                                alt="رسالة صورة" 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  window.open(msg.text, '_blank');
                                }} 
                                style={{ cursor: 'pointer', maxWidth: '200px', borderRadius: '12px', display: 'block', margin: '4px 0' }}
                            />
                        ) : msg.mediaType === 'audio' ? (
                            <div className="audio-player-container" onClick={(e) => e.stopPropagation()}>
                                <audio controls src={msg.text} className="chat-media-audio" style={{ display: 'block', width: '100%', outline: 'none' }} />
                            </div>
                        ) : (
                            <span className="message-text">{msg.text}</span>
                        )}
                        {msg.sender === 'me' && (
                            <div className="message-meta">
                                {renderMessageStatus(msg.status)}
                            </div>
                        )}

                        {/* Bubble Option Menu popup overlay */}
                        {activeOptionMenuMessageId === (msg.id || msg.dbId) && (
                          <div className="message-options-menu" onClick={(e) => e.stopPropagation()}>
                            <button type="button" className="option-item" onClick={() => {
                              const senderName = msg.sender === 'me' ? 'أنا' : (partner || 'الشريك');
                              setReplyingToMessage({
                                id: msg.id || msg.dbId,
                                text: msg.mediaType === 'image' ? '📷 صورة' : (msg.mediaType === 'audio' ? '🎙️ رسالة صوتية' : msg.text),
                                senderName
                              });
                              setActiveOptionMenuMessageId(null);
                            }} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                              <CornerUpLeft size={16} />
                              الرد
                            </button>
                          </div>
                        )}
                    </div>
                 </div>
              ))}

             {typing && (
                 <div className="message-wrapper received">
                     <div className="message-bubble typing-bubble">
                         <div className="typing-dots">
                             <span></span><span></span><span></span>
                         </div>
                     </div>
                 </div>
             )}
             <div ref={messagesEndRef} />
          </div>

           {/* حقل الإدخال */}
           <div className="chat-input-wrapper-outer" style={{ position: 'relative', width: '100%' }}>
             
             {/* Active Reply Preview Bar */}
             {replyingToMessage && (
               <div className="chat-reply-preview-bar">
                 <span className="reply-preview-icon"><CornerUpLeft size={16} /></span>
                 <div className="reply-preview-content">
                   <span className="reply-preview-sender">{replyingToMessage.senderName}</span>
                   <span className="reply-preview-text">{replyingToMessage.text}</span>
                 </div>
                 <button 
                   type="button" 
                   className="reply-preview-close-btn" 
                   onClick={() => setReplyingToMessage(null)}
                 >
                   ×
                 </button>
               </div>
             )}

             {showEmojiPicker && (
               <div className="quick-emoji-picker">
                 {emojis.map((emoji) => (
                   <button
                     key={emoji}
                     type="button"
                     className="emoji-btn"
                     onClick={() => setInputText(prev => prev + emoji)}
                   >
                     {emoji}
                   </button>
                 ))}
               </div>
             )}

             {uploadingMedia && (
               <div className="chat-media-uploading-loader" style={{ position: 'absolute', top: '-40px', left: '0', right: '0', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(10px)', color: '#fff', padding: '8px', fontSize: '0.85rem', borderRadius: '10px', zIndex: 10 }}>
                 <span className="loader-spinner-small" style={{ width: '14px', height: '14px', border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', display: 'inline-block', animation: 'spin 1s linear infinite' }}></span>
                 <span>جاري رفع وتجهيز الملف...</span>
               </div>
             )}

             <input
               type="file"
               accept="image/*"
               ref={photoInputRef}
               style={{ display: 'none' }}
               onChange={handlePhotoSelected}
             />

             {recordedAudioUrl && (
               <audio 
                 ref={previewAudioRef} 
                 src={recordedAudioUrl} 
                 style={{ display: 'none' }} 
                 onEnded={() => setIsPlayingPreview(false)}
               />
             )}

             {/* Voice Preview Player / Main Form toggle */}
             {recordedAudioUrl ? (
               <div className="voice-preview-container">
                 <button type="button" className="voice-preview-btn discard" onClick={discardVoicePreview} title="حذف التسجيل">
                   🗑️
                 </button>
                 <div className="voice-preview-player-bar">
                   <button type="button" className="voice-preview-play-btn" onClick={togglePlayPreview}>
                     {isPlayingPreview ? "⏸️" : "▶️"}
                   </button>
                   <span className="voice-preview-label">
                     {isPlayingPreview ? "جاري الاستماع للرسالة الصوتية..." : "رسالة صوتية جاهزة"} ({formatDuration(recordingDuration)})
                   </span>
                 </div>
                 <button type="button" className="voice-preview-btn send" onClick={sendVoicePreview} title="إرسال التسجيل">
                   📤
                 </button>
               </div>
             ) : isRecording ? (
               <div className="recording-status-bar" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.2)', borderRadius: '14px', backdropFilter: 'blur(10px)', margin: '8px 0' }}>
                 <div className="recording-details" style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#ef4444', fontWeight: 'bold' }}>
                   <span className="recording-pulse-dot" style={{ width: '8px', height: '8px', background: '#ef4444', borderRadius: '50%', display: 'inline-block', animation: 'pulse 1s infinite' }}></span>
                   <span>تسجيل صوتي: {formatDuration(recordingDuration)}</span>
                 </div>
                 <div className="recording-actions-row" style={{ display: 'flex', gap: '10px' }}>
                   <button type="button" className="recording-action-btn cancel" onClick={() => stopVoiceRecording(false)} style={{ background: 'rgba(239, 68, 68, 0.2)', border: 'none', color: '#ef4444', borderRadius: '8px', padding: '6px 12px', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.85rem' }}>❌ إلغاء</button>
                   <button type="button" className="recording-action-btn send" onClick={() => stopVoiceRecording(true)} style={{ background: '#ef4444', border: 'none', color: '#fff', borderRadius: '8px', padding: '6px 12px', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.85rem' }}>⏹️ إيقاف</button>
                 </div>
               </div>
              ) : (
               <form className="message-input-form direct-input-form" onSubmit={handleSendMessage}>
                 
                 {/* Telegram-style input pill */}
                 <div className="chat-input-pill">
                   <button
                     type="button"
                     className={`emoji-toggle-btn ${showEmojiPicker ? 'active' : ''}`}
                     onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                     title="إضافة إيموجي"
                   >
                     <Smile size={22} />
                   </button>

                   <input
                     type="text"
                     placeholder="الرسالة..."
                     value={inputText}
                     onChange={handleInputChange}
                     dir="rtl"
                   />

                   {inputText.trim() === '' && (
                     <div className="attachment-wrapper-relative">
                       <button
                         type="button"
                         className="attachment-trigger-btn"
                         onClick={() => setShowAttachmentMenu(prev => !prev)}
                         title="إرفاق ملف"
                       >
                         <Paperclip size={22} />
                       </button>

                       {showAttachmentMenu && (
                         <div className="attachment-menu-popup">
                           <button
                             type="button"
                             className={`attachment-option ${!user?.isVip ? 'locked' : ''}`}
                             onClick={() => {
                               setShowAttachmentMenu(false);
                               handlePhotoClick();
                             }}
                           >
                             <span className="icon">📷</span>
                             <span className="label">إرسال صورة</span>
                             {!user?.isVip && <span className="lock">🔒</span>}
                           </button>
                           <button
                             type="button"
                             className={`attachment-option ${!user?.isVip ? 'locked' : ''}`}
                             onClick={() => {
                               setShowAttachmentMenu(false);
                               startVoiceRecording();
                             }}
                           >
                             <span className="icon">🎙️</span>
                             <span className="label">رسالة صوتية</span>
                             {!user?.isVip && <span className="lock">🔒</span>}
                           </button>
                         </div>
                       )}
                     </div>
                   )}
                 </div>

                 {/* Circle Action Button */}
                 {inputText.trim() === '' ? (
                   <button
                     type="button"
                     className="chat-circle-action-btn mic"
                     onClick={startVoiceRecording}
                     title="سجل رسالة صوتية"
                   >
                     <Mic size={20} />
                   </button>
                 ) : (
                   <button
                     type="submit"
                     className="chat-circle-action-btn send"
                     title="إرسال"
                   >
                     <Send size={20} />
                   </button>
                 )}
               </form>
             )}
           </div>
        </div>
      )}

      {/* نافذة تأكيد العملية */}
      {confirmDialog && (
        <div className="report-modal-overlay" onClick={() => setConfirmDialog(null)}>
          <div className="report-modal-modern" onClick={e => e.stopPropagation()}
            style={{ maxWidth: 340 }}
          >
            <div className="modal-header-icon">
              <div className="danger-icon-ring" style={{
                color: confirmDialog.type === 'friend' ? '#22c55e' :
                       confirmDialog.type === 'skip' ? '#3b82f6' : '#ef4444',
                fontSize: '2rem'
              }}>
                {confirmDialog.type === 'friend' ? '⚕️' :
                 confirmDialog.type === 'skip' ? '⏭️' : '⛔'}
              </div>
            </div>
            <h2 className="modal-title" style={{ fontSize: '1.1rem', marginBottom: 8 }}>{confirmDialog.title}</h2>
            <p className="modal-subtitle" style={{ marginBottom: 20, fontSize: '0.9rem', lineHeight: 1.5 }}>{confirmDialog.desc}</p>
            <div className="report-actions-modern" style={{ gap: 10 }}>
              <button
                className="btn-submit-danger"
                onClick={() => { confirmDialog.onConfirm(); setConfirmDialog(null); }}
                style={{
                  background: confirmDialog.type === 'friend' ? '#22c55e' :
                              confirmDialog.type === 'skip' ? '#3b82f6' : '#ef4444',
                  color: '#fff', border: 'none', borderRadius: 12, padding: '11px 0',
                  fontWeight: 'bold', width: '100%', cursor: 'pointer', fontSize: '0.95rem'
                }}
              >
                {confirmDialog.type === 'friend' ? '✅ إرسال طلب الصداقة' :
                 confirmDialog.type === 'skip' ? '⏭️ تخطي المحادثة' : '⛔ تأكيد الحظر'}
              </button>
              <button
                className="btn-cancel-modern"
                onClick={() => setConfirmDialog(null)}
                style={{ width: '100%', marginTop: 4 }}
              >
                إلغاء
              </button>
            </div>
          </div>
        </div>
      )}

      {/* نافذة الإبلاغ */}
      <ReportModal
         isOpen={isReportModalOpen}
         onClose={() => setIsReportModalOpen(false)}
         onSubmit={handleReport}
         entityName={partner || 'المستخدم'}
      />

      {/* عرض الملف الشخصي لشركاء الدردشة */}
      {viewProfileUserId && (
        <UserProfile
          userId={viewProfileUserId}
          currentUser={user}
          onClose={() => setViewProfileUserId(null)}
        />
      )}

      {/* نافذة الاشتراك الإجباري */}
      {subscriptionRequiredData && (
          <div className="report-modal-overlay">
              <div className="report-modal-modern">
                  <div className="modal-header-icon">
                      <div className="danger-icon-ring" style={{ color: '#f59e0b' }}>📢</div>
                  </div>
                  <h2 className="modal-title">الاشتراك الإجباري مطلوب</h2>
                  <p className="modal-subtitle">
                      يرجى الانضمام إلى قناتنا الرسمية أولاً لتتمكن من استخدام ميزة المطابقة والدردشة العشوائية.
                  </p>
                  <div className="report-actions-modern">
                      <button
                          className="btn-submit-danger text-center"
                          onClick={() => {
                              if (window.Telegram?.WebApp) {
                                  window.Telegram.WebApp.openTelegramLink(subscriptionRequiredData.channelUrl);
                              } else {
                                  window.open(subscriptionRequiredData.channelUrl, '_blank');
                              }
                          }}
                          style={{ background: '#f59e0b', color: '#fff', border: 'none', borderRadius: '12px', padding: '12px', fontWeight: 'bold', width: '100%', cursor: 'pointer' }}
                      >
                          انضم الآن بالقناة
                      </button>
                      <button
                          className="btn-cancel-modern"
                          onClick={clearSubscriptionError}
                          style={{ width: '100%', marginTop: '10px' }}
                      >
                          إغلاق
                      </button>
                  </div>
              </div>
          </div>
      )}
    </div>
  );
};

export default Match;

